import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { ROUTE_URLS, USER_ROLES } from "../constants/consts";
import { userLogin } from "../api/userLogin";
import {
  clearPreAuthBlockedPage,
  clearLocalStorage,
  getSessionUserRole,
  setPreAuthBlockedPage,
  setSession,
  setSessionUserRole,
  setUserEmail,
  setSessionUserCompany,
  setTwoFactorEnabled,
  setUserId,
  setIsUserLoggedIn,
  setUserLanguage,
  getTempLanguage,
  setTempLanguage,
} from "../api/sessionManagement";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { checkIsBlockedAccess, getBlockPageSettings } from "../api/userBlockedAccess";

const DEFAULT_LANGUAGE = "en-GB";
interface UseSignInReturnValues {
  handleSignIn: (email: string, password: string) => Promise<void>;
}

export const useSignIn = (): UseSignInReturnValues => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { t } = useTranslation();

  const handleSignIn = useCallback(
    async (email: string, password: string) => {
      // Сохраняем язык из URL параметра перед очисткой localStorage
      const savedLanguage = getTempLanguage() || i18n.language;
      clearLocalStorage();
      const userSession = await userLogin(email?.trim() || "", password?.trim() || "");
      if (!userSession.error) {
        if (userSession.data) {
          const userId = userSession.data.data.user_id;
          const isTwoFactorEnabled = userSession.data.data.is_enable_two_factor_auth;
          const isNeedVerify = userSession.data.data.is_need_verify;
          const sessionId = userSession.data.data.session;
          // Приоритет: язык из URL > язык пользователя с сервера > дефолтный язык
          const userLanguage = savedLanguage !== i18n.language ? savedLanguage : 
            (userSession.data.data?.lang?.replace("_", "-") ?? DEFAULT_LANGUAGE);
          
          setUserLanguage(userId, userLanguage);
          localStorage.setItem("i18nextLng", userLanguage);
          await i18n.changeLanguage(userLanguage);

          const [settingsResponse, blockedResponse] = await Promise.all([
            getBlockPageSettings(sessionId),
            checkIsBlockedAccess(sessionId),
          ]);

          const settings = settingsResponse.data?.data ?? [];
          const activePages = settings.filter((page) => page.is_page_active === true);
          const sortedPages = [...activePages].sort((a, b) => a.priority - b.priority);
          const isBlockedAccess = blockedResponse.data?.is_blocked_access ?? false;
          const selectedPage = sortedPages.find((page) => (
            page.priority !== 1 || isBlockedAccess
          ));

          setSession(sessionId);
          setSessionUserCompany(userSession.data.data.company_name);
          setSessionUserRole(userSession.data.data.role);
          setUserEmail(email);
          setIsUserLoggedIn(true);
          setTwoFactorEnabled(isTwoFactorEnabled);
          setUserId(userId);

          if (selectedPage?.text) {
            setPreAuthBlockedPage(selectedPage.text, selectedPage.end_date ?? null);
            return;
          }

          clearPreAuthBlockedPage();
          let userRole: any = getSessionUserRole();
          if (
            isTwoFactorEnabled &&
            isNeedVerify
          ) {
            navigate(ROUTE_URLS.TWO_FACTOR_AUTH);
          } else if (
            userRole === USER_ROLES.ACTIVE
          ) {
            navigate(ROUTE_URLS.HOME);
          } else {
            navigate(ROUTE_URLS.QUESTIONARY);
          }
        }
      } else {
        setTempLanguage(savedLanguage);
        await i18n.changeLanguage(savedLanguage);
        if (userSession.error.response?.status === 404) {
          Swal.fire({
            title: t("oops"),
            text: t("userNotFound"),
            icon: "error",
            confirmButtonText: t("ok"),
            confirmButtonColor: "#0ebfff",
          }).then(() => {});
        } else if (userSession.error.response?.status === 403) {
          Swal.fire({
            title: t("oops"),
            text: t("invalidPassword"),
            icon: "error",
            confirmButtonText: t("ok"),
            confirmButtonColor: "#0ebfff",
          }).then(() => {});
        } else {
          Swal.fire({
            title: t("oops"),
            text: t("signInError"),
            icon: "error",
            confirmButtonText: t("ok"),
            confirmButtonColor: "#0ebfff",
          }).then(() => {});
        }
      }
    },
    [navigate, i18n, t]
  );

  return { handleSignIn };
};
